export interface IUserPrompt {
  prompt: string;
  conversationId?: string;
}

export type Suggestion = {
  text: string;
  prefixEmoji?: string;
};

export type ChatMessageBaseProps = {
  id: string;
  origin: "user" | "bot";
  timestamp: number;
};

export type ChartChatMessage = ChatMessageBaseProps & {
  messageType: "chart";
  content: ChartData;
};

export type TextChatMessage = ChatMessageBaseProps & {
  messageType: "text";
  content: string;
  suggestions?: Suggestion[];
};

export type ReasoningChatMessage = ChatMessageBaseProps & {
  messageType: "reasoning";
  origin: "bot";
  content: string;
};

export type CodeInterpreterChatMessage = ChatMessageBaseProps & {
  messageType: "code_interpreter";
  origin: "bot";
  content: string;
};

export type ChatMessage =
  ChartChatMessage | TextChatMessage | ReasoningChatMessage | CodeInterpreterChatMessage;

export type ResourceStateUnion<T> =
  { status: "loading" } | { status: "error" } | { status: "loaded"; value: T };

export type ChartDataPoint = {
  timestamp: number;
  [key: string]: string | number | undefined;
};

interface ChartOptions {
  responsive?: boolean;
  maintainAspectRatio?: boolean;
  plugins?: {
    legend?: {
      display?: boolean;
      position?: "top" | "bottom" | "left" | "right";
    };
    tooltip?: {
      enabled?: boolean;
    };
  };
  scales?: {
    x?: {
      type?: string;
      display?: boolean;
    };
    y?: {
      type?: string;
      display?: boolean;
      min?: number;
      max?: number;
    };
  };
}

type ChartSeries = {
  key: string;
  color: string;
  valueUnit?: string;
};

export type ChartData = {
  type: "line" | "bar";
  data: ChartDataPoint[];
  options?: ChartOptions;
  title?: string;
  description?: string;
  series: ChartSeries[];
};

export type AgentMessageTextDelta = {
  type: "delta";
  content: string;
};

export type AgentMessageReasoningSummaryTextDelta = {
  type: "reasoning_summary_delta";
  content: string;
  summaryIndex: number;
};

export type AgentMessageTextDone = {
  type: "done";
  finishReason: string;
};

export type AgentMessageNewConversation = {
  type: "new_conversation";
  conversationId: string;
};

export type AgentMessageToolCallInProgress = {
  type: "tool_call_in_progress";
  toolName: string;
  arguments: Record<string, unknown>;
};

export type AgentMessageTooManyPromptsInConversation = {
  type: "too_many_prompts_in_conversation";
  maxPromptsPerConversation: number;
};

export type AgentMessageUnhandledError = {
  type: "unhandled_error";
};

export type AgentMessageListToolsFailed = {
  type: "list_tools_failed";
};

export type AgentMessageToolCallFailed = {
  type: "tool_call_failed";
};

export type AgentMessageToolCallSuccess = {
  type: "tool_call_success";
};

export type AgentMessageTextFinal = {
  type: "final";
  responseTimeMs: number;
};

export type ServerChartDataPoint = {
  timestamp: string;
  value: number;
};

export type ServerChartSeries = {
  legendLabel: string;
  color: string;
  dataPoints: ServerChartDataPoint[];
  valueUnit: string;
};

export type AgentMessageLineChart = {
  type: "charts.line_chart";
  title: string;
  lines: ServerChartSeries[];
};

export type AgentMessageBarChart = {
  type: "charts.bar_chart";
  title: string;
  bars: ServerChartSeries[];
};

export type AgentMessageConversationSubjectUpdated = {
  type: "conversation_subject_updated";
};

export type AgentMessageSuggestions = {
  type: "suggestions";
  suggestions: Suggestion[];
};

export type AgentMessage =
  | AgentMessageNewConversation
  | AgentMessageReasoningSummaryTextDelta
  | AgentMessageTextDelta
  | AgentMessageTextDone
  | AgentMessageToolCallInProgress
  | AgentMessageTooManyPromptsInConversation
  | AgentMessageUnhandledError
  | AgentMessageListToolsFailed
  | AgentMessageToolCallFailed
  | AgentMessageToolCallSuccess
  | AgentMessageLineChart
  | AgentMessageBarChart
  | AgentMessageTextFinal
  | AgentMessageConversationSubjectUpdated
  | AgentMessageSuggestions;

export type NewAgentMessageProps = {
  conversationId: string | undefined;
  message: AgentMessage;
  timestamp: number;
};

export type WidgetDisplayState = "closed" | "opened" | "closingAnimation";
export type ToolCallInProgressState = { status: false } | { status: true; toolName: string };

export interface ISessionState {
  conversationId?: string;
  messages: ResourceStateUnion<ChatMessage[]>;
  isAgentReplyInProgress: boolean;
  partialResponse: string;
  partialReasoning: string;
  reasoningSummaryIndex: number;
  isSessionLocked: boolean;
  toolCallInProgress: ToolCallInProgressState;
}

export interface IChatState {
  conversationHistoryRequestId?: string;
  conversationListRequestId?: string;
  conversationsHistory: ResourceStateUnion<IConversationRecord[]>;
  displayState: WidgetDisplayState;
  isFullScreen: boolean;
  session: ISessionState;
  isSideBarOpen: boolean;
}

export type UserMessage = {
  type: "user_message";
  content: string;
};

export interface ConversationHistoryMessage {
  content: UserMessage | AgentMessage;
  timestamp: string;
}

export interface IConversationHistory {
  messages: Array<ConversationHistoryMessage>;
}

export interface IConversationRecord {
  id: string;
  subject: string | undefined;
  createdAt: number;
  updatedAt: number;
}

export const PythonInterpreterToolName = "execute_python_code";
